import type { TableFeature } from '@tanstack/react-table';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import { type JSX, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import type { LoadingStateFeatureConstructors } from './loading-state-types.js';
import * as styles from './LoadingState.sty.js';
import { ProgressCircle } from '../../../../content/progress/ProgressCircle.js';
import { Flex } from '../../../../layouts/flex/Flex.js';
import { dataTableMessages } from '../../messages.js';

/** Progress circle height breakpoint (px). */
export const PROGRESS_CIRCLE_HEIGHT_BREAKPOINT = 120;

/**
 * Small wrapper that makes sure to only render the defined actions when the menu
 * is open.
 * @internal
 */
export function LoadingState(
  props: Readonly<{ headerHeight: number; scrollOffset: number }>,
): JSX.Element {
  const { headerHeight, scrollOffset } = props;
  const loadingContainerRef = useRef<HTMLDivElement | null>(null);
  const [height, setHeight] = useState<number | undefined>(undefined);
  const intl = useIntl();

  useEffect(() => {
    if (!loadingContainerRef.current) {
      return;
    }

    const resizeObserver = new ResizeObserver((entries) => {
      if (entries.length > 0) {
        setHeight(entries[0].contentRect.height);
      }
    });
    resizeObserver.observe(loadingContainerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <Flex
      ref={loadingContainerRef}
      justifyContent="center"
      alignItems="center"
      className={styles.loadingState}
      style={assignInlineVars({
        [styles.dataTableHeaderHeight]: `${headerHeight}px`,
        [styles.dataTableScrollOffset]: `${scrollOffset}px`,
      })}
    >
      <ProgressCircle
        size={
          height !== undefined && height > PROGRESS_CIRCLE_HEIGHT_BREAKPOINT
            ? 'large'
            : 'small'
        }
        aria-label={intl.formatMessage(dataTableMessages.loadingData)}
      />
    </Flex>
  );
}

export function constructLoadingStateFeature(): TableFeature<LoadingStateFeatureConstructors> {
  return {
    getInitialState: (initialState) => {
      return {
        loading: false,
        ...initialState,
      };
    },
  };
}

/**
 * DataTableLoadingStateFeature is the plugin that provides the loading state for the DataTable,
 * which can be used to display a loading indicator while the table's columns and/or data are being loaded.
 */
export const DataTableLoadingStateFeature = constructLoadingStateFeature();
